import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API } from 'aws-amplify';
import { AppDispatch, RootState } from '../store';
import {
	DEFAULT_SECURITY_SETTINGS,
	SecuritySettings,
	SecuritySettingsRevision,
	readRevision,
	sanitiseSecuritySettings
} from '../../security/securitySettings';
import { reconcileSecuritySettings } from '../../security/bootstrap';

/**
 * Runtime security settings — the store side.
 *
 * The fetch here is not what applies the settings: the bootstrap has already
 * acted on the cached copy before React mounts, because token storage has to be
 * chosen before Amplify is configured. What this does is refresh the cache for
 * the next load, and apply a policy if the cache had not told us to — see
 * `src/security/bootstrap.ts`.
 *
 * So a change Ops make lands on the reader's next load, and a rollback lands the
 * same way. No deploy either direction.
 */

interface SecurityState {
	settings: SecuritySettings;
	loaded: boolean;
	loading: boolean;
	revisions: SecuritySettingsRevision[];
	saving: boolean;
	error: string | null;
}

const initialState: SecurityState = {
	settings: { ...DEFAULT_SECURITY_SETTINGS },
	loaded: false,
	loading: false,
	revisions: [],
	saving: false,
	error: null
};

/**
 * Reads the live settings. Runs on the guest path alongside fetchFeatureFlags:
 * the login screen boots too, and it is the screen where the widget these
 * settings constrain is most likely to be showing.
 */
export const fetchSecuritySettings = createAsyncThunk<
	SecuritySettings,
	void,
	{ dispatch: AppDispatch; state: RootState }
>('security/fetch', async () => {
	const response = await API.get('inspirusapi', '/security-settings', {});
	const settings = sanitiseSecuritySettings(response);
	reconcileSecuritySettings(settings);
	return settings;
});

/**
 * Admin only. Every save creates a new version; nothing is overwritten.
 *
 * Takes a patch rather than the whole document, because two screens own
 * different fields of it: the app-wide policy and token storage belong to
 * System → Security, and the preview origin belongs beside the preview pane it
 * governs. A screen sending the whole document would carry its own stale copy of
 * fields it does not own and quietly revert them.
 *
 * So the current document is read immediately before the write. A concurrent
 * edit between the two is still possible and still handled — the function
 * computes the next version from its own read and answers 409, which surfaces as
 * "reload and save again" rather than a silent overwrite.
 */
export const saveSecuritySettings = createAsyncThunk<
	SecuritySettings,
	Partial<Omit<SecuritySettings, 'version'>>,
	{ dispatch: AppDispatch; state: RootState; rejectValue: string }
>('security/save', async (patch, { rejectWithValue }) => {
	try {
		const current = sanitiseSecuritySettings(await API.get('inspirusapi', '/security-settings', {}));
		const body = {
			cspMode: patch.cspMode ?? current.cspMode,
			cspExtraSources: patch.cspExtraSources ?? current.cspExtraSources,
			tokenStorage: patch.tokenStorage ?? current.tokenStorage,
			previewOrigin: patch.previewOrigin ?? current.previewOrigin
		};
		const response = await API.put('inspirusapi', '/security-settings', { body });
		const settings = sanitiseSecuritySettings(response);
		// Cache it immediately: the admin who saved it is the person most likely to
		// reload straight away to see whether it worked.
		reconcileSecuritySettings(settings);
		return settings;
	} catch (error) {
		const details = (error as { response?: { data?: { details?: string[]; error?: string } } })?.response?.data;
		return rejectWithValue(
			details?.details?.join('. ') || details?.error || 'Could not save the security settings'
		);
	}
});

export const fetchSecuritySettingsRevisions = createAsyncThunk<
	SecuritySettingsRevision[],
	void,
	{ dispatch: AppDispatch; state: RootState }
>('security/fetchRevisions', async () => {
	const response = await API.get('inspirusapi', '/security-settings/revisions', {});
	// Read through, because a backend that predates the paired-directive shape
	// answers with a bare host list — and one absent field crashed the screen.
	// It changes the shape and nothing else: what a version recorded, it keeps.
	return ((response as { revisions?: unknown[] }).revisions ?? []).map(readRevision);
});

const securitySlice = createSlice({
	name: 'security',
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(fetchSecuritySettings.pending, (state) => {
				state.loading = true;
			})
			.addCase(fetchSecuritySettings.fulfilled, (state, action) => {
				state.loading = false;
				state.loaded = true;
				state.settings = action.payload;
			})
			.addCase(fetchSecuritySettings.rejected, (state) => {
				// The bootstrap is already running on the cached settings, and the
				// endpoint answers with defaults rather than an error, so there is
				// nothing to recover here. It must never fail a page.
				state.loading = false;
				state.loaded = true;
			})
			.addCase(saveSecuritySettings.pending, (state) => {
				state.saving = true;
				state.error = null;
			})
			.addCase(saveSecuritySettings.fulfilled, (state, action) => {
				state.saving = false;
				state.settings = action.payload;
			})
			.addCase(saveSecuritySettings.rejected, (state, action) => {
				state.saving = false;
				state.error = action.payload || 'Could not save the security settings';
			})
			.addCase(fetchSecuritySettingsRevisions.fulfilled, (state, action) => {
				state.revisions = action.payload;
			});
	}
});

export default securitySlice.reducer;
